import type { ReactNode } from 'react'
import type { AppointmentDialogOptions } from '@/components/dialogs/appointment-dialog'
import type { VisitDialogOptions } from '@/components/dialogs/visit-dialog'
import type { WhatsAppOptions } from '@/components/dialogs/whatsapp-composer'
import type { PaymentDialogOptions } from '@/components/dialogs/payment-dialog'
import type { FollowUpDialogOptions } from '@/components/dialogs/follow-up-dialog'

import { createContext, useContext, useMemo, useState } from 'react'
import { useClinic } from '@/lib/clinic/store'
import AppointmentDialog from '@/components/dialogs/appointment-dialog'
import VisitDialog from '@/components/dialogs/visit-dialog'
import WhatsAppComposer from '@/components/dialogs/whatsapp-composer'
import PaymentDialog from '@/components/dialogs/payment-dialog'
import FollowUpDialog from '@/components/dialogs/follow-up-dialog'
import FollowUpHistoryDialog from '@/components/dialogs/follow-up-history-dialog'

interface AppDialogsValue {
    openAppointment: (options?: AppointmentDialogOptions) => void
    openVisit: (options: VisitDialogOptions) => void
    openWhatsApp: (options: WhatsAppOptions) => void
    openPayment: (options: PaymentDialogOptions) => void
    openFollowUp: (options: FollowUpDialogOptions) => void
    openFollowUpHistory: (followUpId: string) => void
}

interface DialogState<T> {
    open: boolean
    key: number
    options: T
}

const closed = <T,>(options: T): DialogState<T> => ({ open: false, key: 0, options })

const AppDialogsContext = createContext<AppDialogsValue | null>(null)

// Hosts the dialogs used across many screens (booking, visits, WhatsApp,
// payments, follow-ups) so any page can open them with one call.
export const AppDialogsProvider = ({ children }: { children: ReactNode }): ReactNode => {
    const { ready } = useClinic()
    const [appointment, setAppointment] = useState(closed<AppointmentDialogOptions>({}))
    const [visit, setVisit] = useState(closed<VisitDialogOptions>({ patientId: '' }))
    const [whatsapp, setWhatsApp] = useState(closed<WhatsAppOptions>({ patientId: '' }))
    const [payment, setPayment] = useState(closed<PaymentDialogOptions>({ invoiceId: '' }))
    const [followUp, setFollowUp] = useState(closed<FollowUpDialogOptions>({ mode: 'create' }))
    const [history, setHistory] = useState(closed<string>(''))

    const value = useMemo<AppDialogsValue>(() => {
        const opener = <T,>(setter: (update: (current: DialogState<T>) => DialogState<T>) => void) => (options: T) => {
            setter(current => ({ open: true, key: current.key + 1, options }))
        }
        return {
            openAppointment: (options = {}) => opener(setAppointment)(options),
            openVisit: opener(setVisit),
            openWhatsApp: opener(setWhatsApp),
            openPayment: opener(setPayment),
            openFollowUp: opener(setFollowUp),
            openFollowUpHistory: opener(setHistory)
        }
    }, [])

    const later = (callback: () => void) => window.setTimeout(callback, 200)

    return (
        <AppDialogsContext.Provider value={value}>
            {children}

            {ready && (
                <>
                    <AppointmentDialog
                        key={`appointment-${appointment.key}`}
                        open={appointment.open}
                        onOpenChange={open => setAppointment(current => ({ ...current, open }))}
                        options={appointment.options}
                        onSendConfirmation={item => later(() => value.openWhatsApp({ patientId: item.patientId, kind: 'appointmentConfirmation', appointmentId: item.id }))}
                    />

                    <VisitDialog
                        key={`visit-${visit.key}`}
                        open={visit.open}
                        onOpenChange={open => setVisit(current => ({ ...current, open }))}
                        options={visit.options}
                    />

                    <WhatsAppComposer
                        key={`whatsapp-${whatsapp.key}`}
                        open={whatsapp.open}
                        onOpenChange={open => setWhatsApp(current => ({ ...current, open }))}
                        options={whatsapp.options}
                    />

                    <PaymentDialog
                        key={`payment-${payment.key}`}
                        open={payment.open}
                        onOpenChange={open => setPayment(current => ({ ...current, open }))}
                        options={payment.options}
                    />

                    <FollowUpDialog
                        key={`follow-up-${followUp.key}`}
                        open={followUp.open}
                        onOpenChange={open => setFollowUp(current => ({ ...current, open }))}
                        options={followUp.options}
                        onBookAppointment={(patientId, date) => later(() => value.openAppointment({ patientId, date }))}
                    />

                    <FollowUpHistoryDialog
                        key={`history-${history.key}`}
                        open={history.open}
                        onOpenChange={open => setHistory(current => ({ ...current, open }))}
                        followUpId={history.options}
                    />
                </>
            )}
        </AppDialogsContext.Provider>
    )
}

export const useAppDialogs = (): AppDialogsValue => {
    const context = useContext(AppDialogsContext)
    if (!context) throw new Error('useAppDialogs must be used inside AppDialogsProvider')
    return context
}
